import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, Loader2, Sparkles, Crown, Gift, Tag, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { shouldHideSubscriptionCTAs } from '@/lib/platform';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PLANS = [
  {
    id: 'free',
    name: 'Basic',
    monthly: 0,
    yearly: 0,
    tagline: 'Start sharing memories',
    features: [
      '50 photos per family',
      '5 family members',
      'Text posts, comments & reactions',
      'Family calendar & .ics export',
    ],
  },
  {
    id: 'family',
    name: 'Family Plan',
    monthly: 4.99,
    yearly: 39,
    tagline: 'Most popular',
    featured: true,
    features: [
      'Unlimited photos & videos',
      'Unlimited family members',
      'Extended family with view & comment access',
      'AI photo tagging & search',
      '10% off every photo book & print',
      'Custom themes',
      'Priority email support',
    ],
  },
  {
    id: 'premium',
    name: 'Family Premium',
    monthly: 9.99,
    yearly: 79,
    tagline: 'Everything + keepsakes',
    features: [
      'Everything in Family Plan',
      '$10 credit / mo toward photo books',
      'Priority support (24h response)',
      'Early access to new features',
      'Family time-capsule archives',
    ],
  },
];

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState('yearly');
  const [subscription, setSubscription] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [promoInput, setPromoInput] = useState('');
  const [promoApplying, setPromoApplying] = useState(false);
  const [promo, setPromo] = useState(null); // { code, percent_off, duration, duration_in_months, name }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      toast.success('Processing your subscription…');
      pollStatus(sessionId, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/billing/subscription`, { withCredentials: true })
      .then(res => setSubscription(res.data))
      .catch(() => setSubscription(null));
  }, [user]);

  const pollStatus = async (sessionId, attempts) => {
    if (attempts >= 10) {
      toast.error('Status check timed out. Refresh this page in a minute.');
      return;
    }
    try {
      const res = await axios.get(`${API}/billing/checkout/status/${sessionId}`, { withCredentials: true });
      if (res.data.payment_status === 'paid') {
        toast.success('Subscription active! Welcome to Kinsy.');
        window.history.replaceState({}, '', '/pricing');
        const sub = await axios.get(`${API}/billing/subscription`, { withCredentials: true });
        setSubscription(sub.data);
        return;
      }
      if (res.data.status === 'expired') {
        toast.error('Checkout expired. Please try again.');
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleCheckout = async (planId) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setCheckoutLoading(planId);
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        plan_id: planId,
        billing_cycle: billing,
        origin_url: window.location.origin,
        promo_code: promo?.code,
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
      setCheckoutLoading(null);
    }
  };

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoApplying(true);
    try {
      const { data } = await axios.post(`${API}/billing/validate-promo`, { code });
      setPromo(data);
      toast.success(`${data.name || code} applied`);
    } catch (err) {
      setPromo(null);
      toast.error(err.response?.data?.detail || 'Invalid promo code');
    } finally {
      setPromoApplying(false);
    }
  };

  const removePromo = () => {
    setPromo(null);
    setPromoInput('');
  };

  // Apply the percent_off discount to a base price. Returns a number (USD).
  const discounted = (base) => {
    if (!promo?.percent_off || !base) return base;
    return Math.round(base * (100 - promo.percent_off)) / 100;
  };

  const promoDurationLabel = () => {
    if (!promo) return '';
    if (promo.duration === 'forever') return 'forever';
    if (promo.duration === 'once') return 'for the first payment';
    if (promo.duration === 'repeating') {
      const m = promo.duration_in_months || 0;
      return `for ${m} month${m === 1 ? '' : 's'}`;
    }
    return '';
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-8" data-testid="pricing-page">
      {user ? <Header /> : null}

      <main className="max-w-5xl mx-auto px-4 py-10">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 tracking-tight mb-3">
            Keep every family memory safe
          </h1>
          <p className="text-stone-600 max-w-xl mx-auto">
            Choose a plan that fits your family. Cancel anytime.
          </p>

          <div className="inline-flex items-center gap-1 bg-white border border-stone-200 rounded-full p-1 mt-6" data-testid="billing-toggle">
            <button
              onClick={() => setBilling('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                billing === 'monthly' ? 'bg-stone-900 text-white' : 'text-stone-600'
              }`}
              data-testid="toggle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('yearly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                billing === 'yearly' ? 'bg-stone-900 text-white' : 'text-stone-600'
              }`}
              data-testid="toggle-yearly"
            >
              Yearly <span className="text-teal-500 ml-1">save 35%</span>
            </button>
          </div>

          {/* Promo code field */}
          {user && (
            <div className="mt-6 max-w-md mx-auto" data-testid="promo-section">
              {promo ? (
                <div
                  className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-2 text-sm"
                  data-testid="promo-applied"
                >
                  <Check className="w-4 h-4 text-teal-600 flex-shrink-0" />
                  <span className="flex-1 text-left text-teal-900">
                    <span className="font-semibold">{promo.name || promo.code}</span>{' '}
                    {promo.percent_off ? `— ${promo.percent_off}% off ${promoDurationLabel()}` : 'applied'}
                  </span>
                  <button
                    onClick={removePromo}
                    className="text-teal-700 hover:text-teal-900"
                    aria-label="Remove promo code"
                    data-testid="promo-remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={(e) => { e.preventDefault(); applyPromo(); }}
                  className="flex items-center gap-2"
                  data-testid="promo-form"
                >
                  <div className="flex-1 relative">
                    <Tag className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <Input
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                      placeholder="Have a code? Enter it here"
                      className="h-10 rounded-full pl-9 border-stone-200 bg-white"
                      data-testid="promo-input"
                      autoCapitalize="characters"
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="outline"
                    disabled={promoApplying || !promoInput.trim()}
                    className="h-10 rounded-full border-stone-300"
                    data-testid="promo-apply"
                  >
                    {promoApplying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </Button>
                </form>
              )}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map(plan => {
            const price = billing === 'yearly' ? plan.yearly : plan.monthly;
            const isCurrent = subscription?.plan_id === plan.id && subscription?.status === 'active';
            return (
              <div
                key={plan.id}
                className={`card-soft flex flex-col ${plan.featured ? 'ring-2 ring-teal-500 relative' : ''}`}
                data-testid={`plan-${plan.id}`}
              >
                {plan.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Most popular
                  </div>
                )}

                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    {plan.id === 'premium' ? <Crown className="w-5 h-5 text-amber-500" /> :
                     plan.id === 'family' ? <Sparkles className="w-5 h-5 text-teal-500" /> :
                     <Gift className="w-5 h-5 text-stone-500" />}
                    <h2 className="text-xl font-semibold text-stone-900">{plan.name}</h2>
                  </div>
                  <p className="text-sm text-stone-500">{plan.tagline}</p>
                </div>

                <div className="mb-6">
                  {promo && promo.percent_off && price > 0 ? (
                    <>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-4xl font-bold text-stone-900">${discounted(price).toFixed(2)}</span>
                        <span className="text-sm text-stone-500">
                          {billing === 'yearly' ? '/year' : '/month'}
                        </span>
                      </div>
                      <div className="text-xs text-stone-500 mt-1">
                        <span className="line-through">${price.toFixed(2)}</span>{' '}
                        {promoDurationLabel()}
                        {promo.duration === 'repeating' && (
                          <span>, then ${price.toFixed(2)}{billing === 'yearly' ? '/year' : '/month'}</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-stone-900">${price}</span>
                        <span className="text-sm text-stone-500">
                          {price === 0 ? 'forever' : billing === 'yearly' ? '/year' : '/month'}
                        </span>
                      </div>
                      {billing === 'yearly' && plan.monthly > 0 && (
                        <p className="text-xs text-stone-500 mt-1">
                          ~${(plan.yearly / 12).toFixed(2)}/mo billed annually
                        </p>
                      )}
                    </>
                  )}
                </div>

                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
                      <Check className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  <Button disabled className="w-full rounded-full bg-stone-100 text-stone-500 cursor-default" data-testid={`current-${plan.id}`}>
                    Current plan
                  </Button>
                ) : plan.id === 'free' ? (
                  <Button
                    variant="outline"
                    className="w-full rounded-full"
                    onClick={() => user ? navigate('/') : navigate('/register')}
                    data-testid="free-cta"
                  >
                    {user ? 'Stay on free' : 'Get started'}
                  </Button>
                ) : shouldHideSubscriptionCTAs() ? (
                  // Apple App Store guideline 3.1.1 — digital subscriptions
                  // consumed inside the app must use Apple IAP. Until we wire
                  // StoreKit, tap opens Safari on kinsy.io so the user can
                  // subscribe on the web.
                  <Button
                    variant="outline"
                    className="w-full rounded-full"
                    onClick={() => window.open('https://kinsy.io/pricing', '_blank', 'noopener')}
                    data-testid={`ios-subscribe-open-web-${plan.id}`}
                  >
                    Subscribe on kinsy.io
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleCheckout(plan.id)}
                    disabled={checkoutLoading === plan.id}
                    className={`w-full rounded-full ${plan.featured ? 'bg-teal-600 hover:bg-teal-700 text-white' : 'bg-stone-900 hover:bg-stone-800 text-white'}`}
                    data-testid={`subscribe-${plan.id}`}
                  >
                    {checkoutLoading === plan.id ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</>
                    ) : subscription?.status === 'active' ? 'Switch to this plan' : 'Subscribe'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-center mt-10 text-sm text-stone-500">
          All plans auto-renew. Cancel anytime from your profile. 7-day refund on annual plans.
        </div>
        <div className="text-center mt-4 text-xs text-stone-400">
          <Link to="/privacy" className="hover:text-stone-600">Privacy</Link>
          <span className="mx-2">·</span>
          <Link to="/terms" className="hover:text-stone-600">Terms</Link>
        </div>
      </main>

      {user ? <MobileNav /> : null}
    </div>
  );
}
